import Dexie, { liveQuery, type Table } from 'dexie';
import { useEffect, useState } from 'react';
import type {
  EventRecord,
  ExpenseRecord,
  MessageRecord,
  MetaRecord,
  OutboxItem,
  PhotoRecord,
  ReshootTask,
  StopDto,
  TripDto,
  VehicleCheckRecord,
} from './types';

export type TripRecord = Omit<TripDto, 'stops'> & { updatedAt: string };

/**
 * Дискът на телефона е първичното хранилище, не кеш. Всеки екран чете само
 * оттук; мрежата само допълва. Нищо, което шофьорът е натиснал, не живее
 * единствено в паметта на страницата — Android убива раздела без предупреждение.
 */
class DriverDb extends Dexie {
  trips!: Table<TripRecord, string>;
  stops!: Table<StopDto, string>;
  events!: Table<EventRecord, string>;
  photos!: Table<PhotoRecord, string>;
  expenses!: Table<ExpenseRecord, string>;
  messages!: Table<MessageRecord, string>;
  checks!: Table<VehicleCheckRecord, string>;
  outbox!: Table<OutboxItem, string>;
  meta!: Table<MetaRecord, string>;
  tasks!: Table<ReshootTask, string>;

  constructor() {
    super('kmdx-driver');
    this.version(1).stores({
      trips: 'id, status, plannedStartAt',
      stops: 'id, tripId, [tripId+sequence], status',
      events: 'id, tripId, at, synced, orderStopId',
      photos: 'id, tripId, orderStopId, status, type, sha256',
      expenses: 'id, tripId, spentAt, synced',
      messages: 'id, tripId, sentAt, pending',
      checks: 'id, tripId, at, synced',
      outbox: 'id, status, createdAt, kind, nextAttemptAt',
      meta: 'key',
    });
    /** Задачите „снимай пак" и връзката на новата снимка с върнатия документ. */
    this.version(2).stores({
      photos: 'id, tripId, orderStopId, status, type, sha256, replacesDocumentId',
      tasks: 'documentId, tripId',
    });
  }
}

export const db = new DriverDb();

/** ─── Малки помощници за `meta` ───────────────────────────────────────── */

export async function metaGet<T>(key: string): Promise<T | undefined> {
  const row = await db.meta.get(key);
  return row?.value as T | undefined;
}

export async function metaSet(key: string, value: unknown): Promise<void> {
  await db.meta.put({ key, value });
}

export async function metaDelete(key: string): Promise<void> {
  await db.meta.delete(key);
}

/** Изтрива всичко — при „изход" и при десет сгрешени ПИН-а. */
export async function wipeDatabase(): Promise<void> {
  await db.delete();
  await db.open();
}

/**
 * Реактивно четене от Dexie. `dexie-react-hooks` не е в зависимостите, а
 * самият Dexie вече дава `liveQuery` — двадесет реда вместо още един пакет.
 */
export function useLive<T>(query: () => Promise<T> | T, deps: unknown[], initial: T): T {
  const [value, setValue] = useState<T>(initial);
  useEffect(() => {
    const subscription = liveQuery(query).subscribe({
      next: (next) => setValue(next as T),
      error: (error) => console.error('Грешка при четене от базата', error),
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return value;
}
